import classNames from "classnames";
import { Flex, Link, Table, TableBody, TableCell, TableRow, Text } from "@jsq/ds/core";
import { Icon } from "@jsq/ds/icons";
import { Sizes } from "@jsq/ds/styles";
import { toDbId } from "~/utils/graphql-helpers";
import * as styles from "./styles_vanilla.css";
import { type TaxDetailItem } from "./utils";

export type TaxDetailsTableProps = {
  taxDetails: TaxDetailItem[];
};

const buildPositionsUrl = (assignments: TaxDetailItem["sourceDocumentAssignments"]) => {
  if (!assignments || assignments.length === 0) return "/canopy/compliance/positions";

  const arenaIds = assignments
    .map((a) => a.arenaId)
    .filter((id): id is number => id !== undefined && id > 0);
  const positionIds = assignments
    .map((a) => {
      if (!a.positionId) return undefined;
      try {
        const dbId = toDbId(a.positionId);
        if (Number.isNaN(dbId)) {
          const parsed = parseInt(a.positionId, 10);
          return Number.isNaN(parsed) ? undefined : parsed;
        }
        return dbId;
      } catch {
        const parsed = parseInt(a.positionId, 10);
        return Number.isNaN(parsed) ? undefined : parsed;
      }
    })
    .filter((id): id is number => id !== undefined && id > 0);

  if (arenaIds.length > 0 && positionIds.length > 0) {
    return `/canopy/compliance/positions?arenaIds=${arenaIds.join(",")}&positionIds=${positionIds.join(",")}`;
  }
  return "/canopy/compliance/positions";
};

const renderValueCell = (detail: TaxDetailItem) => {
  if (detail.isExpired) {
    return (
      <Flex alignItems="center" gap={Sizes.SM}>
        <Text variant="regular-md">{detail.displayValue}</Text>
        <Flex alignItems="center" gap={Sizes.XS} className={styles.expiredWarning}>
          <Icon type="exclamation-triangle" variant="outline" size="small" />
          <Text variant="regular-md" className={styles.expiredWarning}>
            Document has expired
          </Text>
        </Flex>
      </Flex>
    );
  }

  if (detail.sourceDocumentAssignments && detail.sourceDocumentAssignments.length > 0) {
    const positionsUrl = buildPositionsUrl(detail.sourceDocumentAssignments);

    return (
      <Flex flexDirection="column" gap={Sizes.XS} alignItems="flex-start">
        <Link
          href={positionsUrl}
          className={styles.positionsLink}
          icon="external-link"
          iconPosition="right"
          iconSize="small"
          shouldOpenInNewTab
        >
          {detail.displayValue}
        </Link>
        {detail.sourceDocumentAssignments.map((assignment) => (
          <Text
            key={`${assignment.investorName}-${assignment.positionId ?? assignment.arenaId}`}
            variant="regular-md"
          >
            <Text asSpan variant="strong-md">
              {assignment.investorName}
            </Text>
            {assignment.entityName && (
              <>
                <Text asSpan variant="secondary-md">
                  {" in "}
                </Text>
                {assignment.entityName}
              </>
            )}
          </Text>
        ))}
      </Flex>
    );
  }

  if (detail.showCheckmark) {
    return (
      <Flex alignItems="flex-start" gap={Sizes.SM}>
        <Icon type="check" size="small" className={styles.checkmarkIcon} />
        <Flex flexDirection="column" gap={Sizes.XS}>
          <Text variant="regular-md">{detail.displayValue}</Text>
          {detail.linkedValue && (
            <Text variant="regular-md" className={styles.linkedValueText}>
              {detail.linkedValue}
            </Text>
          )}
        </Flex>
      </Flex>
    );
  }

  const valueVariant = detail.isSecondaryValue ? "secondary-md" : "regular-md";
  return <Text variant={valueVariant}>{detail.hasValue ? detail.displayValue : "--"}</Text>;
};

export const TaxDetailsTable = ({ taxDetails }: TaxDetailsTableProps) => {
  return (
    <Table variant="muted" condensed>
      <TableBody>
        {taxDetails.map((detail, index) => {
          const showBorder = detail.isLastInGroup;
          const prevItem = index > 0 ? taxDetails[index - 1] : undefined;
          const isAfterBorder = prevItem?.isLastInGroup;

          return detail.isSectionHeader ? (
            <TableRow key={detail.key}>
              <TableCell colSpan={2} className={styles.sectionHeaderCell}>
                <Text variant="strong-md">{detail.label}</Text>
              </TableCell>
            </TableRow>
          ) : (
            <TableRow key={detail.key}>
              <TableCell
                className={classNames(styles.tableCellStyle, {
                  [styles.sectionLastRowCell]: showBorder,
                  [styles.sectionFirstRowCell]: isAfterBorder,
                })}
              >
                <Text variant="secondary-md">{detail.label}</Text>
              </TableCell>
              <TableCell
                className={classNames(styles.valueCellStyle, {
                  [styles.sectionLastRowCell]: showBorder,
                  [styles.sectionFirstRowCell]: isAfterBorder,
                })}
              >
                {renderValueCell(detail)}
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
};
